import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, FileText, Pencil, Upload, Loader2, X, Sparkles, Save, Send, RefreshCw, Eye, AlertTriangle, CheckCircle2, ExternalLink, Braces, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { AuthTemplateEditor, DEFAULT_AUTH, AuthEligibilityNotice } from "@/components/AuthTemplateEditor";
import { CopyToChannelButton } from "@/components/CopyToChannelButton";
import { DynamicFieldsDialog, insertAtCursor } from "@/components/templates/DynamicFieldsDialog";
import { CarouselEditor } from "@/components/templates/CarouselEditor";
import { ImageCropDialog } from "@/components/templates/ImageCropDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import WhatsAppPreview, { exampleValue } from "@/components/WhatsAppPreview";

const CATEGORIES = [
  { v: "marketing", label: "Marketing" },
  { v: "utility", label: "Utility" },
  { v: "service", label: "Servizio" },
  { v: "authentication", label: "OTP / Autenticazione" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.label]));
const STATUS = {
  approved: { label: "Approvato", cls: "bg-[#10B981]/15 text-[#10B981]" },
  pending: { label: "In valutazione", cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  rejected: { label: "Rifiutato", cls: "bg-[#EF4444]/15 text-[#EF4444]" },
};
const HEADER_TYPES = [
  { v: "none", label: "Nessuna" },
  { v: "text", label: "Testo" },
  { v: "image", label: "Immagine" },
  { v: "document", label: "Documento PDF" },
  { v: "video", label: "Video" },
];
const BTN_TYPES = [
  { v: "quick_reply", label: "Testo (risposta rapida)" },
  { v: "url", label: "Link" },
  { v: "phone", label: "Telefono" },
  { v: "unsubscribe", label: "Disiscriviti" },
  { v: "catalog", label: "Catalogo" },
];
export const FIXED_BTN_TEXT = { unsubscribe: "Disiscriviti", catalog: "Vedi catalogo" };
const VARS = ["{{nome}}", "{{sconto}}", "{{scadenza}}", "{{codice}}"];
const LANGS = [{ v: "it", label: "Italiano" }, { v: "en_US", label: "Inglese (US)" }, { v: "es", label: "Spagnolo" }, { v: "fr", label: "Francese" }, { v: "de", label: "Tedesco" }];
const EMPTY = { name: "", category: "marketing", body: "", footer: "", meta_language: "it", header: { type: "none", text: "", media_url: "" }, buttons: [], carousel: [], link_tracking: "none", click_tag: "", click_cluster_id: null, click_automation_id: null };
const detectVars = (body) => Array.from((body || "").matchAll(/\{\{([^{}|]+)(?:\|[^{}]*)?\}\}/g)).map((m) => m[1].trim()).filter(Boolean);

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/templates/${id}`); toast.success("Template eliminato"); load(); };
  const setStatus = async (t, status) => { await api.put(`/templates/${t.id}/status`, { status }); toast.success("Stato aggiornato"); load(); };
  const syncMeta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/templates/sync-meta");
      toast.success(`Stato sincronizzato da Meta (${data.updated} template)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sincronizzazione non riuscita");
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Template</h1>
          <p className="text-slate-400 text-sm mt-1">Intestazione, corpo con campi dinamici, piè di pagina, pulsanti e carosello — con categoria e stato Meta</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={syncMeta} disabled={syncing} data-testid="sync-meta-button" variant="outline"
                  className="bg-[#1E293B] border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#334155]">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Sincronizza con Meta
          </Button>
          <FooterDialog />
          <Button asChild data-testid="add-template-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            <Link to="/template/nuovo"><Plus className="h-4 w-4 mr-2" /> Nuovo template</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((t) => {
          const st = STATUS[t.status] || STATUS.pending;
          return (
            <div key={t.id} data-testid={`template-card-${t.id}`}
                 className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 hover:border-[#10B981]/40 transition-all flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-[#10B981]/15 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-[#10B981]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-200 truncate">{t.name}</p>
                    <Badge className="bg-[#1E293B] text-slate-400 border-0 text-xs mt-0.5">{CAT_LABEL[t.category] || t.category}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/template/${t.id}`} data-testid={`edit-template-${t.id}`} className="text-slate-500 hover:text-[#3B82F6] p-1" title="Modifica"><Pencil className="h-4 w-4" /></Link>
                  <MetaCheckDialog template={t} />
                  <CopyToChannelButton endpoint={`/templates/${t.id}/copy-to`} itemName={t.name} testId={`copy-template-${t.id}`} note="La copia arriva come bozza: nel nuovo canale va richiesta a Meta (WABA diverso)." />
                  <button onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`} className="text-slate-500 hover:text-[#EF4444] p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              <p className="text-sm text-slate-400 leading-relaxed bg-[#0A0E17] rounded-lg p-3 min-h-[70px] flex-1">{t.body || "—"}</p>
              {t.status === "rejected" && (
                <div className="mt-2 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2 text-xs text-[#FCA5A5]" data-testid={`template-reject-reason-${t.id}`}>
                  <b>Rifiutato da Meta</b>{t.meta_reason ? `: ${t.meta_reason}` : " (motivo non indicato)"}. Correggi il template e premi di nuovo "Richiedi a Meta".
                </div>
              )}
              {t.meta_status === "PAUSED" && (
                <div className="mt-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#FCD34D]" data-testid={`template-paused-${t.id}`}>
                  <b>Sospeso da Meta</b>{t.meta_reason ? `: ${t.meta_reason}` : ""} — qualità bassa: non può essere inviato finché Meta non lo riattiva.
                </div>
              )}
              {t.header_mismatch && (
                <div className="mt-2 rounded-lg border border-[#EF4444]/50 bg-[#EF4444]/10 px-3 py-2 text-xs text-[#FCA5A5] flex gap-2" data-testid={`template-header-mismatch-${t.id}`}>
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span><b>Intestazione non allineata con Meta.</b> {t.header_mismatch}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-1 mt-3">
                {t.header?.type && t.header.type !== "none" && <Badge className="bg-[#3B82F6]/15 text-[#3B82F6] border-0 text-[10px]">Header: {t.header.type}</Badge>}
                {t.buttons?.length > 0 && <Badge className="bg-[#53BDEB]/15 text-[#53BDEB] border-0 text-[10px]">{t.buttons.length} pulsanti</Badge>}
                {t.carousel?.length > 0 && <Badge className="bg-[#EC4899]/15 text-[#EC4899] border-0 text-[10px]">Carosello {t.carousel.length}</Badge>}
                {t.variables?.map((v) => <Badge key={v} className="bg-[#8B5CF6]/15 text-[#8B5CF6] border-0 text-[10px] font-mono2">{`{{${v}}}`}</Badge>)}
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1F2937]">
                <div className="flex flex-col gap-0.5">
                  <Badge data-testid={`template-status-${t.id}`} className={`${st.cls} border-0 text-xs w-fit`}>{st.label}{t.meta_status && !["APPROVED", "REJECTED", "PENDING"].includes(t.meta_status) ? ` · ${t.meta_status}` : ""}</Badge>
                  {t.meta_status_at && <span className="text-[10px] text-slate-500" data-testid={`template-status-at-${t.id}`}>da Meta il {new Date(t.meta_status_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>}
                </div>
                <Select value={t.status || "pending"} onValueChange={(v) => setStatus(t, v)}>
                  <SelectTrigger className="h-7 w-[130px] bg-[#0A0E17] border-[#1F2937] text-xs" data-testid={`template-status-select-${t.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="approved">Approvato</SelectItem>
                    <SelectItem value="pending">In valutazione</SelectItem>
                    <SelectItem value="rejected">Rifiutato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
        {templates.length === 0 && <p className="text-slate-500 col-span-full text-center py-10">Nessun template. Creane uno nuovo.</p>}
      </div>
    </div>
  );
}

function MetaCheckDialog({ template }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/templates/${template.id}/meta-check`).then((r) => setData(r.data)).catch((e) => setData({ warnings: [{ level: "error", text: formatApiErrorDetail(e.response?.data?.detail) }], payload: null }));
  }, [open, template.id]);
  const LV = { error: "bg-[#EF4444]/10 border-[#EF4444]/40 text-[#EF4444]", warn: "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#F59E0B]", info: "bg-[#38BDF8]/10 border-[#38BDF8]/30 text-[#38BDF8]" };
  const cards = data?.payload?.template?.components?.find((c) => c.type === "carousel")?.cards || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`meta-check-${template.id}`} title="Vedi cosa invieremo a Meta" className="text-slate-500 hover:text-[#10B981] p-1"><Eye className="h-4 w-4" /></button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B1120] border-[#1F2937] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="meta-check-dialog">
        <DialogHeader><DialogTitle className="font-display">Cosa invieremo a Meta · {template.name}</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#10B981]" /></div> : (
          <div className="space-y-4">
            {data.warnings?.length ? data.warnings.map((w, i) => (
              <div key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${LV[w.level] || LV.info}`} data-testid={`meta-check-warning-${w.level}`}>
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{w.text}</span>
              </div>
            )) : (
              <div className="flex items-center gap-2 rounded-lg border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-2 text-xs text-[#10B981]" data-testid="meta-check-ok">
                <CheckCircle2 className="h-4 w-4" /> Template allineato con Meta: pronto per l'invio.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl border border-[#1F2937] p-3 space-y-1">
                <p className="text-slate-500 uppercase tracking-wide text-[10px]">Anteprima locale</p>
                <p>Schede carosello: <span className="text-slate-200">{data.local?.cards ?? 0}</span></p>
                <p>Header: <span className="text-slate-200">{data.local?.header}</span> · Pulsanti: <span className="text-slate-200">{data.local?.buttons ?? 0}</span></p>
              </div>
              <div className="rounded-xl border border-[#1F2937] p-3 space-y-1">
                <p className="text-slate-500 uppercase tracking-wide text-[10px]">Template su Meta</p>
                {data.meta ? (<>
                  <p>Stato: <span className="text-slate-200">{data.meta.status}</span> · Lingua: <span className="text-slate-200">{data.meta.language}</span></p>
                  <p>Carosello: <span className={data.meta.has_carousel ? "text-[#10B981]" : "text-[#EF4444]"}>{data.meta.has_carousel ? `sì (${data.meta.cards} schede)` : "assente"}</span> · Header: <span className="text-slate-200">{data.meta.header_format || "—"}</span></p>
                </>) : <p className="text-slate-500">Non disponibile</p>}
              </div>
            </div>
            {cards.length > 0 && (
              <div className="space-y-1.5" data-testid="meta-check-cards">
                <p className="text-xs text-slate-400">Schede che verranno inviate</p>
                {cards.map((c) => {
                  const img = c.components.find((x) => x.type === "header")?.parameters?.[0]?.image?.link;
                  const btns = c.components.filter((x) => x.type === "button");
                  return (
                    <div key={c.card_index} className="flex items-center gap-3 rounded-lg border border-[#1F2937] p-2 text-xs">
                      {img ? <img src={img} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-[#1F2937]" />}
                      <span className="text-slate-300">Scheda {c.card_index + 1}</span>
                      <span className="text-slate-500">{btns.length ? `${btns.length} pulsante/i quick reply` : "pulsanti link statici (dal template Meta)"}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {data.payload && (
              <details className="rounded-xl border border-[#1F2937]">
                <summary className="cursor-pointer px-3 py-2 text-xs text-slate-400 hover:text-slate-200">Payload JSON completo</summary>
                <pre className="px-3 pb-3 text-[10px] text-[#10B981] font-mono2 overflow-x-auto max-h-64" data-testid="meta-check-payload">{JSON.stringify(data.payload, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FooterDialog() {
  const [open, setOpen] = useState(false);
  const [footer, setFooter] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/app-settings").then((r) => setFooter(r.data.default_footer || "")).catch(() => {}); }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/app-settings", { default_footer: footer });
      toast.success("Firma predefinita salvata");
      setOpen(false);
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="footer-settings-button" className="bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
          <FileText className="h-4 w-4 mr-2" /> Firma mittente
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Firma mittente predefinita</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Piè di pagina applicato automaticamente ai nuovi template</Label>
          <Textarea rows={2} data-testid="default-footer-input" value={footer} onChange={(e) => setFooter(e.target.value)}
                    className="bg-[#0A0E17] border-[#1F2937] resize-none" placeholder="Es. Azienda S.r.l. · Rispondi STOP per disiscriverti" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-footer-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva firma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MB = 1024 * 1024;
const BrandLogoCtx = createContext({ url: "", upload: null });

function MediaUpload({ value, onChange, accept, label, testid, maxBytes, onMeta, crop }) {
  const brand = useContext(BrandLogoCtx);
  const [busy, setBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const readImageMeta = (file) => new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight, size: file.size, type: file.type }); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve({ size: file.size, type: file.type }); URL.revokeObjectURL(url); };
    img.src = url;
  });
  const upload = async (file, meta) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/media", fd);
      const m = meta ?? (await readImageMeta(file));
      onChange(data.url, m);   // URL e metadati in un solo aggiornamento: evita che una seconda scrittura con stato obsoleto cancelli l'URL
      onMeta?.(m);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (crop && file.type.startsWith("image/")) { setCropFile(file); return; } // il ritaglio produce sempre un JPG entro i limiti
    if (maxBytes && file.size > maxBytes) {
      toast.error(`File troppo grande (${(file.size / MB).toFixed(1)} MB): il massimo consentito da Meta è ${Math.round(maxBytes / MB)} MB`);
      return;
    }
    await upload(file);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1E293B] border border-[#1F2937] text-slate-200 text-sm cursor-pointer hover:bg-[#334155]" data-testid={testid}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
          <input type="file" accept={accept} hidden onChange={onFile} />
        </label>
        {value && <button type="button" onClick={() => { onChange(""); onMeta?.(null); }} className="text-slate-500 hover:text-[#EF4444]"><X className="h-4 w-4" /></button>}
      </div>
      {value && <p className="text-xs text-slate-500 truncate font-mono2">{value}</p>}
      {crop && cropFile && <ImageCropDialog file={cropFile} open onCancel={() => setCropFile(null)} onConfirm={(f, meta) => { setCropFile(null); upload(f, meta); }} logoUrl={brand.url} onLogoUpload={brand.upload} />}
    </div>
  );
}

const BTN_LIMITS = { total: 10, url: 2, phone: 1, quick_reply: 10, unsubscribe: 1, catalog: 1 };
const BTN_LABEL = { url: "Link", phone: "Telefono", quick_reply: "Testo", unsubscribe: "Disiscriviti", catalog: "Catalogo" };
const BTN_HINT = {
  quick_reply: "Il cliente tocca e invia il testo del pulsante in chat: puoi collegarlo a un'automazione.",
  url: "Apre una pagina web. Massimo 2 per template; attiva il tracciamento per contare i clic.",
  phone: "Avvia una chiamata al numero indicato. Massimo 1 per template.",
  unsubscribe: "Risposta rapida di disiscrizione richiesta da Meta per il Marketing: testo fisso «Disiscriviti», il contatto viene disiscritto automaticamente come con STOP.",
  catalog: "Apre il catalogo prodotti collegato al tuo account WhatsApp Business (va configurato su Meta). Testo fisso «Vedi catalogo», deve essere l'unico pulsante, solo categoria Marketing. Facoltativo: codice SKU del prodotto da mostrare come miniatura.",
};
const isQuick = (b) => ["quick_reply", "unsubscribe"].includes(b.type || "quick_reply");
const btnLive = (b) => (b.text || "").trim() || FIXED_BTN_TEXT[b.type];

export function validateButtons(buttons, category = "marketing") {
  const live = (buttons || []).filter(btnLive);
  if (live.length > BTN_LIMITS.total) return `Meta ammette al massimo ${BTN_LIMITS.total} pulsanti per template`;
  for (const k of ["url", "phone", "quick_reply", "unsubscribe", "catalog"]) {
    const n = live.filter((b) => (b.type || "quick_reply") === k).length;
    if (n > BTN_LIMITS[k]) return `Meta ammette al massimo ${BTN_LIMITS[k]} pulsant${BTN_LIMITS[k] === 1 ? "e" : "i"} «${BTN_LABEL[k]}»`;
  }
  if (live.some((b) => b.type === "catalog")) {
    if (live.length > 1) return "Il pulsante «Catalogo» deve essere l'unico pulsante del template (regola Meta)";
    if (category !== "marketing") return "Il pulsante «Catalogo» è ammesso solo nei template Marketing";
    return null;
  }
  let groups = 0, prev = null;
  for (const b of live) { const q = isQuick(b); if (q !== prev) { groups += 1; prev = q; } }
  if (groups > 2) return "Meta richiede che i pulsanti Testo/Disiscriviti stiano insieme, separati dai pulsanti Link/Telefono";
  return null;
}

function ButtonsEditor({ buttons, onChange, category = "marketing" }) {
  const list = buttons || [];
  const count = (k) => list.filter((b) => (b.type || "quick_reply") === k).length;
  const canAdd = list.length < BTN_LIMITS.total && !list.some((b) => b.type === "catalog");
  const add = () => { if (!canAdd) return; const type = count("quick_reply") < BTN_LIMITS.quick_reply ? "quick_reply" : count("url") < BTN_LIMITS.url ? "url" : "phone"; onChange([...list, { type, text: "", value: "" }]); };
  const set = (i, patch) => onChange(list.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const setType = (i, type) => set(i, { type, text: FIXED_BTN_TEXT[type] || (FIXED_BTN_TEXT[list[i].type] ? "" : list[i].text), value: "" });
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  const error = validateButtons(list, category);
  const typeDisabled = (t, b) => t.v !== b.type && (count(t.v) >= BTN_LIMITS[t.v] || (t.v === "catalog" && (list.length > 1 || category !== "marketing")));
  return (
    <div className="space-y-2">
      {list.map((b, i) => (
        <div key={i} className="space-y-1">
        <div className="flex items-center gap-2">
          <Select value={b.type} onValueChange={(v) => setType(i, v)}>
            <SelectTrigger className="h-9 w-40 bg-[#0A0E17] border-[#1F2937] text-xs shrink-0" data-testid={`tpl-btn-type-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
              {BTN_TYPES.map((t) => <SelectItem key={t.v} value={t.v} disabled={typeDisabled(t, b)} data-testid={`tpl-btn-type-opt-${t.v}`}>{t.label}{typeDisabled(t, b) ? (t.v === "catalog" && category !== "marketing" ? " (solo Marketing)" : t.v === "catalog" ? " (unico pulsante)" : " (limite raggiunto)") : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <span title={BTN_HINT[b.type]} className="text-slate-500 hover:text-slate-300 shrink-0" data-testid={`tpl-btn-info-${i}`}><Info className="h-3.5 w-3.5" /></span>
          <Input value={FIXED_BTN_TEXT[b.type] || b.text} onChange={(e) => set(i, { text: e.target.value.slice(0, 25) })} placeholder="Testo pulsante" maxLength={25}
                 disabled={!!FIXED_BTN_TEXT[b.type]} className="bg-[#0A0E17] border-[#1F2937] h-9 flex-1 disabled:opacity-70" data-testid={`tpl-btn-text-${i}`} />
          {!isQuick(b) && (
            <Input value={b.value} onChange={(e) => set(i, { value: e.target.value })}
                   placeholder={b.type === "url" ? "https://..." : b.type === "phone" ? "+39..." : "SKU miniatura (opz.)"} className="bg-[#0A0E17] border-[#1F2937] h-9 w-40" data-testid={`tpl-btn-value-${i}`} />
          )}
          <button type="button" onClick={() => del(i)} className="text-slate-500 hover:text-[#EF4444]" data-testid={`tpl-btn-del-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
        {(b.type === "unsubscribe" || b.type === "catalog") && <p className="text-[11px] text-slate-500 pl-1" data-testid={`tpl-btn-hint-${i}`}>{BTN_HINT[b.type]}</p>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={add} disabled={!canAdd} className="text-xs text-[#10B981] hover:underline disabled:text-slate-500 disabled:no-underline" data-testid="tpl-btn-add">+ Aggiungi pulsante</button>
        <span className="text-[11px] text-slate-500" data-testid="tpl-btn-limits">{list.length}/{BTN_LIMITS.total} · link {count("url")}/{BTN_LIMITS.url} · telefono {count("phone")}/{BTN_LIMITS.phone} · testo {count("quick_reply") + count("unsubscribe")}/{BTN_LIMITS.quick_reply}</span>
      </div>
      {list.length >= 4 && <p className="text-[11px] text-amber-400" data-testid="tpl-btn-many-warning">Con 4 o più pulsanti WhatsApp mostra i primi 2 e un menu «Vedi tutte le opzioni»; il template non è visualizzabile su WhatsApp Desktop.</p>}
      {error && <p className="text-xs text-red-400" data-testid="tpl-btn-error">{error}</p>}
    </div>
  );
}

export function TemplateEditorBody({ existing, onDone, onBack }) {
  const open = true;
  const [form, setForm] = useState(EMPTY);
  const [carouselOn, setCarouselOn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [customVars, setCustomVars] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [testLinks, setTestLinks] = useState(null); // risultato /templates/{id}/test-links
  const [authBlock, setAuthBlock] = useState(null); // rifiuto Meta 2388185 (idoneità WABA ai template OTP)
  const [testingLink, setTestingLink] = useState(false);
  const [catCheck, setCatCheck] = useState(null);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [brandLogo, setBrandLogo] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setBrandLogo(r.data?.brand_logo_url || "")).catch(() => {}); }, []);
  const uploadBrandLogo = async (file) => {
    if (file.size > 2 * MB) { toast.error("Logo troppo grande: massimo 2 MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload/media", fd);
      await api.put("/settings", { brand_logo_url: data.url });
      setBrandLogo(data.url);
      toast.success("Logo insegna salvato");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Caricamento logo non riuscito"); }
  };
  const bodyRef = useRef(null);
  const cardRefs = useRef({});
  const activeCardRef = useRef(0);
  const target = useRef({ kind: "body" });
  const openFields = (kind, idx) => { target.current = { kind, idx }; setFieldsOpen(true); };
  const pickField = (token) => {
    const { kind, idx } = target.current;
    if (kind === "card") {
      const el = cardRefs.current[idx];
      const { value, pos } = insertAtCursor(el, form.carousel[idx]?.body || "", token);
      setForm((f) => ({ ...f, carousel: f.carousel.map((c, i) => (i === idx ? { ...c, body: value } : c)) }));
      setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(pos, pos); } }, 0);
      return;
    }
    const el = bodyRef.current;
    const { value, pos } = insertAtCursor(el, form.body || "", token);
    setForm((f) => ({ ...f, body: value.slice(0, 1000) }));
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(pos, pos); } }, 0);
  };

  useEffect(() => {
    if (!open) return;
    const txt = `${form.body || ""} ${form.header?.text || ""}`.trim();
    if (txt.length < 12) { setCatCheck(null); return; }
    const t = setTimeout(() => {
      api.post("/templates/category-check", { category: form.category, body: form.body, header_text: form.header?.text || "", footer: form.footer,
        buttons: form.buttons || [], carousel: carouselOn ? form.carousel : [] }).then((r) => setCatCheck(r.data)).catch(() => setCatCheck(null));
    }, 600);
    return () => clearTimeout(t);
  }, [open, form.category, form.body, form.header?.text, form.footer, form.buttons, form.carousel, carouselOn]);

  useEffect(() => {
    if (open) {
      setSavedId(existing?.id || null);
      setTestLinks(null);
      setAuthBlock(null);
      if (existing?.id && existing?.category === "authentication" && existing?.meta_last_error?.kind === "AUTH_TEMPLATE_NOT_ELIGIBLE") {
        api.get(`/templates/${existing.id}/otp-ticket`).then((r) => setAuthBlock(r.data)).catch(() => {});
      }
      api.get("/contacts/custom-fields").then((r) => setCustomVars(r.data || [])).catch(() => setCustomVars([]));
      api.get("/clusters").then((r) => setClusters(r.data || [])).catch(() => setClusters([]));
      api.get("/automations").then((r) => setAutomations(r.data || [])).catch(() => setAutomations([]));
      const base = existing ? { ...EMPTY, ...existing, header: { ...EMPTY.header, ...(existing.header || {}) } } : EMPTY;
      setForm(base);
      setCarouselOn((existing?.carousel || []).length > 0);
      if (!existing) {
        api.get("/app-settings").then((r) => {
          if (r.data.default_footer) setForm((f) => ({ ...f, footer: r.data.default_footer }));
        }).catch(() => {});
      }
    }
  }, [open, existing]);

  const insertVar = (v) => pickField(v);
  const setHeader = (patch) => setForm((f) => ({ ...f, header: { ...f.header, ...patch } }));
  const vars = Array.from(new Set(detectVars(form.body)));

  const suggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/templates/suggest", { category: form.category, hint: form.name });
      setForm((f) => ({ ...f, body: data.text }));
      toast.success("Messaggio suggerito dall'AI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suggerimento non disponibile");
    } finally { setAiBusy(false); }
  };

  const validateCarousel = (cards) => {
    const live = cards.filter((c) => c.image_url || (c.body || "").trim() || (c.buttons || []).length);
    if (!live.length) return null;
    for (let i = 0; i < cards.length; i++) {
      if ((cards[i].body || "").length > 160) return `Elemento ${i + 1}: testo di ${cards[i].body.length} caratteri, il massimo consentito da Meta è 160`;
    }
    if (live.length < 2) return "Il carosello richiede almeno 2 schede";
    let sig0 = null;
    for (let i = 0; i < live.length; i++) {
      const c = live[i];
      if (!c.image_url) return `Elemento ${i + 1}: manca l'immagine`;
      if (!(c.body || "").trim()) return `Elemento ${i + 1}: manca il testo`;
      const btns = (c.buttons || []).filter((b) => (b.text || "").trim() || b.type === "unsubscribe");
      if (!btns.length) return `Elemento ${i + 1}: aggiungi almeno un pulsante (Meta lo richiede)`;
      if (btns.length > 2) return `Elemento ${i + 1}: massimo 2 pulsanti per elemento`;
      for (let j = 0; j < btns.length; j++) {
        if (btns[j].type === "catalog") return `Elemento ${i + 1}: il pulsante «Catalogo» non è ammesso nel carosello`;
        if (btns[j].type === "url" && !/^https?:\/\/\S+\.\S+/.test((btns[j].value || "").trim())) return `Elemento ${i + 1}, pulsante ${j + 1}: inserisci il link (https://...) del pulsante`;
        if (btns[j].type === "phone" && !(btns[j].value || "").trim()) return `Elemento ${i + 1}, pulsante ${j + 1}: inserisci il numero di telefono`;
      }
      const sig = btns.map((b) => b.type || "quick_reply").join(",");
      if (sig0 === null) sig0 = sig;
      else if (sig !== sig0) return `Elemento ${i + 1}: tutti gli elementi devono avere gli stessi pulsanti dell'elemento 1 (${sig0.split(",").length} pulsanti dello stesso tipo)`;
    }
    return null;
  };
  const carouselError = carouselOn ? validateCarousel(form.carousel) : null;

  const persist = async () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return null; }
    const isAuth = form.category === "authentication";
    if (isAuth) {
      const a = { ...DEFAULT_AUTH, ...(form.auth || {}) };
      const m = a.code_expiration_minutes;
      if (m !== null && m !== "" && (Number(m) < 1 || Number(m) > 90)) { toast.error("Scadenza codice: da 1 a 90 minuti"); return null; }
      if (!a.otp_button?.text?.trim() || a.otp_button.text.length > 25) { toast.error("Testo del pulsante OTP obbligatorio (max 25)"); return null; }
      const payload = { ...form, auth: a, body: "", footer: "", header: { type: "none", text: "", media_url: "" }, buttons: [], carousel: [], variables: [], link_tracking: "none" };
      const id0 = existing?.id || savedId;
      if (id0) { await api.put(`/templates/${id0}`, payload); return id0; }
      const { data } = await api.post("/templates", payload);
      setSavedId(data.id);
      return data.id;
    }
    if (!form.body.trim() && !(carouselOn && form.carousel.length)) { toast.error("Inserisci un corpo messaggio o un carosello"); return null; }
    if (carouselError) { toast.error(carouselError); return null; }
    const buttonsError = validateButtons(form.buttons, form.category);
    if (buttonsError) { toast.error(buttonsError); return null; }
    const payload = { ...form, variables: detectVars(form.body), carousel: carouselOn ? form.carousel : [] };
    const id = existing?.id || savedId;
    if (id) { await api.put(`/templates/${id}`, payload); return id; }
    const { data } = await api.post("/templates", payload);
    setSavedId(data.id);
    return data.id;
  };

  const testLink = async (idx) => {
    setTestingLink(true);
    try {
      const id = await persist();
      if (!id) return;
      const { data } = await api.post(`/templates/${id}/test-links`);
      const row = data.links[idx];
      if (!row) { toast.error("Pulsante Link non trovato: salva il pulsante con un URL valido"); setTestLinks(data); return; }
      window.open(row.test_url, "_blank", "noopener");
      toast.success("Clic di prova registrato: il link si apre in una nuova scheda");
      setTimeout(async () => {
        try { const r2 = await api.post(`/templates/${id}/test-links`); setTestLinks(r2.data); } catch { setTestLinks(data); }
      }, 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Prova link non riuscita");
    } finally { setTestingLink(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const id = await persist();
      if (!id) return;
      toast.success(existing ? "Template aggiornato" : "Bozza salvata");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const requestMeta = async () => {
    setRequesting(true);
    try {
      const id = await persist();
      if (!id) return;
      const { data } = await api.post(`/templates/${id}/request-meta`);
      toast.success(`Richiesta inviata a Meta (${data.meta_status}) · nome: ${data.meta_name}`);
      if (data.ignored?.length) toast.info(`Carosello: Meta non ammette ${data.ignored.join(", ")} → esclusi dalla richiesta`, { duration: 8000 });
      if (data.renamed_from) toast.info(`Su Meta esisteva già "${data.renamed_from}": richiesto come "${data.meta_name}"`, { duration: 8000 });
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "AUTH_TEMPLATE_NOT_ELIGIBLE") {
        setAuthBlock(d);
        toast.error(d.message, { duration: 10000 });
        return;
      }
      toast.error(formatApiErrorDetail(d) || "Invio a Meta non riuscito");
    } finally { setRequesting(false); }
  };

  const actions = (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <Button type="button" onClick={saveDraft} disabled={saving || requesting} data-testid="save-template-button"
              variant="outline" className="bg-[#1E293B] border-[#1F2937] text-slate-200">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Salva bozza</>}
      </Button>
      <Button type="button" onClick={requestMeta} disabled={saving || requesting} data-testid="request-template-button"
              className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
        {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Richiedi a Meta</>}
      </Button>
    </div>
  );

  return (
    <BrandLogoCtx.Provider value={{ url: brandLogo, upload: uploadBrandLogo }}>
    <div className="space-y-5" data-testid="template-editor-page">
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-b border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-2">
        <button type="button" onClick={onBack} className="text-slate-500 hover:text-slate-900 p-1" data-testid="template-editor-back" title="Torna ai template"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-2 min-w-0">
          <span className="truncate">{existing ? "Modifica template" : "Richiedi template"}</span>
          <Badge className="bg-slate-100 text-slate-600 border-0 text-xs shrink-0">{existing?.meta_status ? ({ approved: "Approvato", rejected: "Rifiutato", pending: "In valutazione" }[existing.meta_status] || existing.meta_status) : "Bozza"}</Badge>
        </h1>
        <div className="w-full sm:w-auto sm:ml-auto flex justify-end">{actions}</div>
      </div>
      {existing?.header_mismatch && (
        <div className="rounded-xl border border-[#EF4444]/50 bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B] flex gap-3 items-start" data-testid="template-editor-header-mismatch">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Approvato da Meta con un'intestazione diversa da questa</p>
            <p className="text-xs mt-0.5">{existing.header_mismatch} Premi «Richiedi a Meta»: verrà creata una nuova versione (es. _v2) da selezionare poi nelle campagne.</p>
          </div>
        </div>
      )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Colonna sinistra: dati */}
          <div className="lg:col-span-3 space-y-4">
            <div className="space-y-2"><Label><span className="text-[#EF4444]">*</span> Nome</Label>
              <Input data-testid="template-name-input" maxLength={90} value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-[#111827] border-[#1F2937]" placeholder="Es. offerta_settimanale" />
              <p className="text-[10px] text-slate-500 text-right">{form.name.length}/90</p>
            </div>
            <div className="space-y-2"><Label><span className="text-[#EF4444]">*</span> Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#111827] border-[#1F2937]" data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                  {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {catCheck && form.category !== "authentication" && (catCheck.suggestion || (catCheck.reasons || []).length > 0) && (
                <div className={`rounded-lg border p-2.5 space-y-1.5 text-[11px] ${catCheck.suggestion === "utility" ? "border-[#10B981]/50 bg-[#10B981]/5" : "border-[#F59E0B]/50 bg-[#F59E0B]/5"}`} data-testid="category-suggestion">
                  <p className={`font-medium text-xs flex items-center gap-1 ${catCheck.suggestion === "utility" ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                    <Sparkles className="h-3.5 w-3.5" />
                    {catCheck.suggestion === "utility" ? `Potrebbe essere Utility: ${catCheck.saving_pct != null ? `−${catCheck.saving_pct}% di costo Meta` : "costo Meta ridotto"}`
                      : catCheck.suggestion === "marketing" ? "Rischio riclassifica come Marketing" : "Contenuto misto"}
                  </p>
                  {(catCheck.reasons || []).map((r, i) => <p key={i} className="text-slate-400">{r}</p>)}
                  <p className="text-slate-500">Marketing €{catCheck.unit_marketing}/msg · Utility €{catCheck.unit_utility}/msg ({catCheck.source === "meta" ? "tariffe reali Meta" : "stima"})</p>
                  {catCheck.suggestion && (
                    <button type="button" onClick={() => setForm({ ...form, category: catCheck.suggestion })} data-testid="category-suggestion-apply"
                            className={`px-2 py-1 rounded-md text-[11px] font-medium ${catCheck.suggestion === "utility" ? "bg-[#10B981] text-[#0A0E17]" : "bg-[#F59E0B] text-[#0A0E17]"}`}>
                      Passa a {catCheck.suggestion === "utility" ? "Utility" : "Marketing"}
                    </button>
                  )}
                  <p className="text-slate-600">{catCheck.rules}</p>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Lingua</Label>
              <Select value={form.meta_language} onValueChange={(v) => setForm({ ...form, meta_language: v })}>
                <SelectTrigger className="bg-[#111827] border-[#1F2937]" data-testid="template-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                  {LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.category !== "authentication" && (<>
            <div className="space-y-2 border-t border-[#1F2937] pt-3">
              <div className="flex items-center justify-between"><Label>Carosello</Label>
                <Switch checked={carouselOn} onCheckedChange={setCarouselOn} data-testid="template-carousel-toggle" /></div>
              <p className="text-[11px] text-slate-500">Più schede scorrevoli con immagine, testo e pulsanti.</p>
              {carouselOn && <p className="text-[11px] text-[#F59E0B]" data-testid="carousel-meta-hint">Con il carosello Meta ammette solo testo + schede: intestazione, piè di pagina e pulsanti principali vengono esclusi dalla richiesta. Testo scheda max 160 caratteri.</p>}
            </div>
            <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-2" data-testid="link-tracking-box">
              <Label>Tracciamento clic sui pulsanti Link</Label>
              <Select value={form.link_tracking || "none"} onValueChange={(v) => setForm({ ...form, link_tracking: v })}>
                <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="link-tracking-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                  <SelectItem value="none">Nessuno (link diretto)</SelectItem>
                  <SelectItem value="campaign">Per campagna: conta i clic totali</SelectItem>
                  <SelectItem value="contact">Per contatto: vedi chi ha cliccato e quando</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                {form.link_tracking === "contact" ? "Il link del pulsante passa dall'app con un codice per destinatario (URL dinamico su Meta). Richiede l'approvazione Meta del template."
                  : form.link_tracking === "campaign" ? "Il link del pulsante passa dall'app e viene contato per campagna (non sai chi ha cliccato). Richiede l'approvazione Meta del template."
                    : "I clic non vengono rilevati: WhatsApp non comunica chi apre i link."}
              </p>
              {form.link_tracking === "contact" && (
                <div className="space-y-2 border-t border-[#1F2937] pt-3" data-testid="click-actions-box">
                  <Label className="text-xs text-[#F59E0B]">Azioni automatiche al clic</Label>
                  <Input value={form.click_tag || ""} onChange={(e) => setForm({ ...form, click_tag: e.target.value })}
                         placeholder="Assegna tag, es. Ha cliccato volantino" className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="click-tag-input" />
                  <Select value={form.click_cluster_id || "none"} onValueChange={(v) => setForm({ ...form, click_cluster_id: v === "none" ? null : v })}>
                    <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="click-cluster-select"><SelectValue placeholder="Iscrivi a un cluster" /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                      <SelectItem value="none">Nessun cluster</SelectItem>
                      {clusters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500">Chi clicca il link riceve il tag e/o entra nel cluster: potrai ricontattarlo con un'offerta mirata.</p>
                  <Select value={form.click_automation_id || "none"} onValueChange={(v) => setForm({ ...form, click_automation_id: v === "none" ? null : v })}>
                    <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="click-automation-select"><SelectValue placeholder="Al clic avvia automazione" /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                      <SelectItem value="none">Nessuna automazione al clic</SelectItem>
                      {automations.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.trigger_type === "link_click" ? " · clic su link" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500">Al primo clic del contatto parte l'automazione scelta: il messaggio viene inviato solo se la finestra 24h è aperta (regola Meta).</p>
                </div>
              )}
              {form.link_tracking !== "none" && (() => {
                const rows = [];
                if (!carouselOn) (form.buttons || []).forEach((b, i) => { if (b.type === "url") rows.push({ label: `Pulsante principale ${i + 1}: ${b.text || "—"}`, url: b.value }); });
                else (form.carousel || []).forEach((c, ci) => (c.buttons || []).forEach((b, bi) => { if (b.type === "url") rows.push({ label: `Scheda ${ci + 1} · pulsante ${bi + 1}: ${b.text || "—"}`, url: b.value }); }));
                return (
                  <div className="space-y-1.5 border-t border-[#1F2937] pt-3" data-testid="tracked-links-summary">
                    <Label className="text-xs text-[#10B981]">Link tracciati che verranno generati ({rows.length})</Label>
                    {rows.length === 0 ? (
                      <p className="text-[11px] text-slate-500" data-testid="tracked-links-empty">Nessun pulsante di tipo Link ancora: aggiungi pulsanti "Apri link" {carouselOn ? "nelle schede del carosello" : "nella sezione Pulsanti"} e qui vedrai i link tracciati.</p>
                    ) : rows.map((r, i) => (
                      <div key={i} className="text-[11px] rounded-md bg-[#0A0E17] border border-[#1F2937] px-2 py-1.5" data-testid={`tracked-link-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-slate-300">{r.label}</p>
                            <p className="text-slate-500 truncate">→ {r.url || "(URL mancante)"}</p>
                            <p className="text-[#10B981] font-mono2 truncate">{form.link_tracking === "contact" ? "…/api/r/<codice>/<contatto>" : "…/api/r/<codice>"} <span className="text-slate-600">codice diverso per ogni pulsante</span></p>
                          </div>
                          <button type="button" onClick={() => testLink(i)} disabled={testingLink || !r.url} data-testid={`test-link-${i}`}
                                  className="shrink-0 px-2 py-1 rounded-md border border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10 disabled:opacity-40 flex items-center gap-1">
                            {testingLink ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />} Prova link
                          </button>
                        </div>
                        {testLinks?.links?.[i] && (
                          <p className="text-slate-400 mt-1" data-testid={`test-link-result-${i}`}>
                            Codice <span className="font-mono2 text-slate-300">{testLinks.links[i].code}</span> · clic di prova registrati: <b className="text-[#3B82F6]">{testLinks.links[i].test_clicks}</b> <span className="text-slate-600">(esclusi dalle statistiche)</span>
                          </p>
                        )}
                      </div>
                    ))}
                    <p className="text-[11px] text-slate-500">Non devi creare nulla a mano: alla "Richiesta a Meta" ogni pulsante Link riceve il suo codice. I clic sono conteggiati separatamente per pulsante/scheda nel report campagna ("Dettaglio per link"). "Prova link" salva la bozza, apre il redirect reale e registra un clic demo.</p>
                  </div>
                );
              })()}
            </div>
            </>)}
          </div>

          {form.category === "authentication" ? <>{authBlock && <AuthEligibilityNotice info={authBlock} />}<AuthTemplateEditor auth={form.auth} language={form.meta_language} onChange={(auth) => setForm({ ...form, auth })} /></> : (<>
          {/* Colonna centrale: editor */}
          <div className="lg:col-span-6 space-y-4">
            <div className="space-y-2 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
              <div className="flex items-center justify-between"><Label>Intestazione</Label><span className="text-xs text-slate-500">Opzionale</span></div>
              <Select value={form.header.type} onValueChange={(v) => setHeader({ type: v })}>
                <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="template-header-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                  {HEADER_TYPES.map((h) => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.header.type === "text" && (
                <Input value={form.header.text} onChange={(e) => setHeader({ text: e.target.value })} data-testid="template-header-text"
                       className="bg-[#0A0E17] border-[#1F2937]" placeholder="Testo intestazione" />
              )}
              {form.header.type === "image" && <MediaUpload crop value={form.header.media_url} onChange={(u) => setHeader({ media_url: u })} accept="image/jpeg,image/png" label="Carica immagine (JPG/PNG, max 5 MB)" maxBytes={5 * 1024 * 1024} testid="template-header-image-upload" />}
              {form.header.type === "document" && <MediaUpload value={form.header.media_url} onChange={(u) => setHeader({ media_url: u })} accept=".pdf" label="Carica PDF (max 100 MB)" maxBytes={100 * 1024 * 1024} testid="template-header-doc-upload" />}
              {form.header.type === "video" && <MediaUpload value={form.header.media_url} onChange={(u) => setHeader({ media_url: u })} accept="video/mp4,video/3gpp" label="Carica video (MP4, max 16 MB)" maxBytes={16 * 1024 * 1024} testid="template-header-video-upload" />}
            </div>

            <div className="space-y-2 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Testo</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => openFields("body")} data-testid="template-insert-field"
                          className="h-8 bg-[#0A0E17] border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10">
                    <Braces className="h-3.5 w-3.5 mr-1" /> Campo dinamico
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={suggest} disabled={aiBusy} data-testid="template-ai-suggest"
                          className="h-8 bg-[#0A0E17] border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/10">
                    {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} Suggerisci messaggio
                  </Button>
                </div>
              </div>
              <Textarea ref={bodyRef} data-testid="template-body-input" rows={6} maxLength={1000} value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        className="bg-[#0A0E17] border-[#1F2937] resize-none" placeholder="Testo messaggio in Italiano" />
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {VARS.map((v) => (
                    <button key={v} type="button" onClick={() => insertVar(v)}
                            className="px-2.5 py-1 rounded-md text-xs font-mono2 bg-[#8B5CF6]/15 text-[#8B5CF6] hover:bg-[#8B5CF6]/25">{v}</button>
                  ))}
                  {customVars.map((v) => (
                    <button key={v} type="button" onClick={() => insertVar(`{{${v}}}`)} data-testid={`template-var-${v}`}
                            title="Campo personalizzato"
                            className="px-2.5 py-1 rounded-md text-xs font-mono2 bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25">{`{{${v}}}`}</button>
                  ))}
                </div>
                <span className="text-[10px] text-slate-500">{(form.body || "").length}/1000</span>
              </div>
              <p className="text-[10px] text-slate-500">Suggerimento: <span className="font-mono2">{"{{campo|valore di riserva}}"}</span> mostra un testo predefinito se il campo del cliente è vuoto.</p>
            </div>

            <div className="space-y-2 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
              <div className="flex items-center justify-between"><Label>Piè di pagina</Label><span className="text-xs text-slate-500">Opzionale</span></div>
              <Input data-testid="template-footer-input" maxLength={60} value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" placeholder="Es. Rispondi STOP per disiscriverti" />
              <p className="text-[10px] text-slate-500">{(form.footer || "").length}/60</p>
            </div>

            <div className="space-y-2 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
              <Label>Pulsanti call-to-action</Label>
              <ButtonsEditor buttons={form.buttons} category={form.category} onChange={(b) => setForm({ ...form, buttons: b })} />
            </div>

            {carouselOn && (
              <div className="space-y-3 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
                <div className="flex items-center justify-between"><Label>Carosello</Label><span className="text-xs text-slate-500">2–10 elementi · stessa struttura di pulsanti</span></div>
                <CarouselEditor cards={form.carousel} onChange={(carousel) => setForm((f) => ({ ...f, carousel }))} MediaUpload={MediaUpload}
                                onInsertField={() => openFields("card", Math.min(activeCardRef.current, form.carousel.length - 1))}
                                registerTextarea={(i, el) => { cardRefs.current[i] = el; if (el) activeCardRef.current = i; }} />
                {carouselError && (
                  <p className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg px-3 py-2" data-testid="carousel-error">{carouselError}</p>
                )}
                <p className="text-[10px] text-slate-500">Regole Meta: ogni elemento ha un'immagine, un testo fino a 160 caratteri e 1-2 pulsanti; tutti gli elementi hanno gli stessi pulsanti; i Link possono essere inviati come shortlink tracciati singolarmente.</p>
              </div>
            )}
          </div>

          {/* Colonna destra: anteprima + valori */}
          <div className="lg:col-span-3 space-y-4 lg:sticky lg:top-0 self-start">
            <div><Label className="text-xs text-slate-500 mb-2 block">Anteprima</Label>
              <WhatsAppPreview body={form.body} header={form.header} footer={form.footer}
                               buttons={form.buttons} carousel={carouselOn ? form.carousel : []} /></div>
            <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-3">
              <Label className="text-xs text-slate-400">Valori di esempio</Label>
              {vars.length === 0 ? (
                <p className="text-xs text-slate-500 mt-2">Non ci sono campi dinamici.</p>
              ) : (
                <div className="space-y-1.5 mt-2" data-testid="template-example-values">
                  {vars.map((v) => (
                    <div key={v} className="flex items-center justify-between gap-2 text-xs" data-testid={`template-example-${v}`}>
                      <Badge className="bg-[#8B5CF6]/15 text-[#8B5CF6] border-0 text-[10px] font-mono2 shrink-0">{`{{${v}}}`}</Badge>
                      <span className="text-slate-500">→</span>
                      <span className="text-slate-200 truncate flex-1 text-right">{exampleValue(v)}</span>
                    </div>
                  ))}
                  <p className="text-[10px] text-slate-500 pt-1">Nell'anteprima le variabili sono sostituite da valori realistici di esempio.</p>
                </div>
              )}
            </div>
          </div>
          </>)}
        </div>

      <div className="flex justify-end pb-6">{actions}</div>
      <DynamicFieldsDialog open={fieldsOpen} onOpenChange={setFieldsOpen} customFields={customVars} onPick={pickField} />
    </div>
    </BrandLogoCtx.Provider>
  );
}
